import { Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "@/components/Navigation";
import { LegalLinks } from "@/components/LegalPages";

const WHATSAPP_NUMBER = "31638617073";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20TTW%20Engineering%2C%20I%20have%20a%20question%20about%20chiptuning.`;

export const WhatsAppButton = () => (
  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn" aria-label="Chat on WhatsApp"
    className="fixed bottom-20 right-4 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#20BD5A] hover:scale-110 transition-all"
  >
    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

const AutotunerSection = () => (
  <section data-testid="autotuner-section" className="py-12 px-4 bg-[#0A0A0A] border-t border-[#1A1A1A]">
    <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">
      <p className="text-[#4A4A4A] text-xs uppercase tracking-[0.3em]">Powered by</p>
      <a href="https://www.autotuner.com" target="_blank" rel="noopener noreferrer" data-testid="autotuner-logo-link" className="transition-all hover:scale-105 hover:opacity-90">
        <img src="https://www.autotuner.com/cdn/shop/files/autotuner-logo_e54ab439-b7d8-4fb4-bbf1-ca6127d78910.png?v=1730211585&width=418" alt="AutoTuner" className="h-12 sm:h-14 w-auto" data-testid="autotuner-logo-img" />
      </a>
    </div>
  </section>
);

export default function Footer({ onOpenTerms, onOpenPrivacy }) {
  const currentYear = new Date().getFullYear();
  return (
    <>
      <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-[#1A1A1A] py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-8">
            <Logo />
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 text-center md:text-left">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-[#FF3B30] mt-1 flex-shrink-0" />
                <div className="text-[#8A8A8A]"><p>Wartelstraat 16</p><p>8223EH Lelystad</p></div>
              </div>
              <a href="tel:+31638617073" data-testid="footer-phone" className="flex items-center gap-3 text-[#8A8A8A] hover:text-[#FF3B30] transition-colors">
                <Phone size={20} className="text-[#FF3B30]" /><span>+31 6 38617073</span>
              </a>
              <a href="mailto:ttw.engineering26@gmail.com" data-testid="footer-email" className="flex items-center gap-3 text-[#8A8A8A] hover:text-[#FF3B30] transition-colors">
                <Mail size={20} className="text-[#FF3B30]" /><span>ttw.engineering26@gmail.com</span>
              </a>
            </div>
            <LegalLinks onOpenTerms={onOpenTerms} onOpenPrivacy={onOpenPrivacy} />

            {/* Payment & Shipping logos */}
            <div className="flex flex-col items-center gap-3 pt-2" data-testid="footer-trust-badges">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <span className="text-[#444] text-[10px] uppercase tracking-widest">Betalen met</span>
                {[
                  { name: "iDEAL", src: "/logos/payment/ideal.png" },
                  { name: "Visa", src: "/logos/payment/visa.png" },
                  { name: "Mastercard", src: "/logos/payment/mastercard.png" },
                  { name: "Bancontact", src: "/logos/payment/bancontact.svg" },
                ].map((m) => (
                  <img key={m.name} src={m.src} alt={m.name} title={m.name} className="h-8 w-auto" />
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <span className="text-[#444] text-[10px] uppercase tracking-widest">Verzending via</span>
                {[
                  { name: "PostNL", src: "/logos/shipping/postnl.svg", h: "h-7" },
                  { name: "DHL", src: "/logos/shipping/dhl.svg", h: "h-5" },
                  { name: "DPD", src: "/logos/shipping/dpd.svg", h: "h-7" },
                  { name: "UPS", src: "/logos/shipping/ups.svg", h: "h-7" },
                ].map((c) => (
                  <img key={c.name} src={c.src} alt={c.name} title={c.name} className={`${c.h} w-auto`} />
                ))}
              </div>
            </div>

            <p className="text-[#4A4A4A] text-sm">&copy; {currentYear} TTW Engineering. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
}
